import { cn } from 'helpers/cn';
import './ai-chat.css';

/**
 * - idle: gentle breathing, the default everywhere.
 * - thinking: a continuous staggered bounce, the request is sent and nothing has come back.
 * - writing: a fast voice meter, the answer is being written.
 * - still: no motion, for the brand logo and list rows.
 *
 * Hover (one bounce) is not a state: it comes from an enclosing `.ai-trigger`.
 */
export type LogoBarsState = 'idle' | 'thinking' | 'writing' | 'still';

/** The sizes the handoff uses: 11 inline and in rows, 12 buttons and menus, 14 headers, 15 toolbar. */
export type LogoBarsSize = 11 | 12 | 14 | 15;

export type LogoBarsProps = {
  state?: LogoBarsState;
  size?: LogoBarsSize;
  className?: string;
};

const STATE_CLASS: Record<LogoBarsState, string | undefined> = {
  idle: undefined,
  thinking: 'is-thinking',
  writing: 'is-writing',
  still: 'is-still',
};

/**
 * The AI mark: three bars sized by font-size. Always decorative; the control it sits in carries
 * the label, and state changes are announced through a live region, never by the bars alone.
 * Reduced motion stops every animation (ai-chat.css).
 */
export const LogoBars = ({ state = 'idle', size = 12, className }: LogoBarsProps) => (
  <span
    aria-hidden="true"
    data-state={state}
    className={cn('ai-bars', STATE_CLASS[state], className)}
    style={{ fontSize: size }}
  >
    <i />
    <i />
    <i />
  </span>
);

/**
 * The glow's halo, the first child of an `.ai-glow` element. Its blur is on this wrapper and the
 * masked ring is its child, as handoff 3.3 requires.
 */
export const GlowHalo = () => (
  <span className="ai-glow__halo" aria-hidden="true">
    <i />
  </span>
);
